import React, { createContext, useState, useCallback, ReactNode, useMemo, useEffect } from 'react';
import { useLocation } from 'react-router-dom';

export interface DrawerConfig {
  title: string | ReactNode;
  content: ReactNode;
  width?: number | string;
  onClose?: () => void;
  footer?: ReactNode;
  maskClosable?: boolean;
  closable?: boolean;
  keyboard?: boolean;
  placement?: 'top' | 'right' | 'bottom' | 'left';
  zIndex?: number;
}

export interface DrawerContextType {
  isOpen: boolean;
  config: DrawerConfig | null;
  openDrawer: (config: DrawerConfig) => void;
  closeDrawer: () => void;
  updateConfig: (config: Partial<DrawerConfig>) => void;
}

export const DrawerContext = createContext<DrawerContextType>({
  isOpen: false,
  config: null,
  openDrawer: () => {},
  closeDrawer: () => {},
  updateConfig: () => {},
});

interface DrawerProviderProps {
  children: ReactNode;
}

export const DrawerProvider: React.FC<DrawerProviderProps> = ({ children }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [config, setConfig] = useState<DrawerConfig | null>(null);
  const location = useLocation();

  const openDrawer = useCallback((drawerConfig: DrawerConfig) => {
    setConfig(drawerConfig);
    setIsOpen(true);
  }, []);

  const closeDrawer = useCallback(() => {
    setIsOpen(false);
    // 延迟清除配置，确保动画完成
    setTimeout(() => {
      setConfig((prevConfig) => {
        prevConfig?.onClose?.();
        return null;
      });
    }, 300);
  }, []);

  const updateConfig = useCallback((newConfig: Partial<DrawerConfig>) => {
    setConfig(prevConfig => prevConfig ? { ...prevConfig, ...newConfig } : null);
  }, []);

  // 监听路由变化，自动关闭 drawer
  useEffect(() => {
    if (isOpen) {
      closeDrawer();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  const contextValue = useMemo(() => ({
    isOpen,
    config,
    openDrawer,
    closeDrawer,
    updateConfig,
  }), [isOpen, config, openDrawer, closeDrawer, updateConfig]);

  return (
    <DrawerContext.Provider value={contextValue}>
      {children}
    </DrawerContext.Provider>
  );
};
